import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import SelectBox from "../../BaseComponents/SelectBox/SelectBox";
import styles from "./RegisterRefundModal.module.css";

const emptySupplierForm = {
  supplierName: "",
  bookingRef: "",
  componentType: "",
  account: "",
  paymentMethod: "",
  amount: "",
  currency: "EUR",
  date: "",
  reason: "",
  airlinePnr: "",
  supplierReferenceNo: "",
  transactionId: "",
  notes: "",
};

const emptyClientForm = {
  customerName: "",
  bookingRef: "",
  account: "",
  paymentMethod: "",
  amount: "",
  currency: "EUR",
  date: "",
  reason: "",
  airlinePnr: "",
  supplierReferenceNo: "",
  transactionId: "",
  notes: "",
};

const supplierReasons = [
  { label: "Select reason", value: "" },
  { label: "Cancellation", value: "Cancellation" },
  { label: "Change", value: "Change" },
  { label: "Overpayment", value: "Overpayment" },
];

const clientReasons = [
  { label: "Select reason", value: "" },
  { label: "Booking Cancellation", value: "Booking Cancellation" },
  { label: "Traveler Cancellation", value: "Traveler Cancellation" },
  { label: "Credit Note Payout", value: "Credit Note Payout" },
];

const componentTypes = [
  { label: "Select component", value: "" },
  { label: "Flight", value: "Flight" },
  { label: "Hotel", value: "Hotel" },
  { label: "Transfer", value: "Transfer" },
  { label: "Insurance", value: "Insurance" },
  { label: "Car Rental", value: "Car Rental" },
  { label: "Visa", value: "Visa" },
];

const paymentMethods = [
  { label: "Select method", value: "" },
  { label: "Bank Transfer", value: "Bank Transfer" },
  { label: "SEPA", value: "SEPA" },
  { label: "Visa", value: "Visa" },
  { label: "Mastercard", value: "Mastercard" },
  { label: "Cash", value: "Cash" },
];

const accounts = [
  { label: "Select account", value: "" },
  { label: "Main Business Account", value: "Main Business Account" },
  { label: "Cash Account", value: "Cash Account" },
  { label: "Credit Card Processing", value: "Credit Card Processing" },
];

const RegisterRefundModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  onSave,
  saveLoading = false,
  refundType = "supplier",
}) => {
  const isSupplier = refundType === "supplier";
  const emptyForm = isSupplier ? emptySupplierForm : emptyClientForm;
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      if (isSupplier) {
        setForm({
          supplierName: initialData.supplierName || "",
          bookingRef: initialData.bookingRef || "",
          componentType: initialData.componentType || "",
          account: initialData.account || "",
          paymentMethod: initialData.paymentMethod || "",
          amount: initialData.amount?.toString() || "",
          currency: initialData.currency || "EUR",
          date: initialData.date || "",
          reason: initialData.reason || "",
          airlinePnr: initialData.airlinePnr || "",
          supplierReferenceNo: initialData.supplierReferenceNo || "",
          transactionId: initialData.transactionId || "",
          notes: initialData.notes || "",
        });
      } else {
        setForm({
          customerName: initialData.customerName || "",
          bookingRef: initialData.bookingRef || "",
          account: initialData.account || "",
          paymentMethod: initialData.paymentMethod || "",
          amount: initialData.amount?.toString() || "",
          currency: initialData.currency || "EUR",
          date: initialData.date || "",
          reason: initialData.reason || "",
          airlinePnr: initialData.airlinePnr || "",
          supplierReferenceNo: initialData.supplierReferenceNo || "",
          transactionId: initialData.transactionId || "",
          notes: initialData.notes || "",
        });
      }
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, initialData, isSupplier]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (isSupplier && !form.supplierName.trim()) return;
    if (!isSupplier && !form.customerName.trim()) return;
    if (!form.bookingRef.trim()) return;
    if (!form.amount || parseFloat(form.amount) <= 0) return;
    if (!form.date) return;
    if (!form.reason) return;

    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      refundType,
    };

    onSave(payload);
  };

  const title = mode === "edit"
    ? `Edit ${isSupplier ? "Supplier" : "Client"} Refund`
    : `Register ${isSupplier ? "Supplier" : "Client"} Refund`;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      width="640px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saveLoading}>
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading && <span className={styles.spinner} />}
            {mode === "edit" ? "Update Refund" : "Register Refund"}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        {isSupplier ? (
          <div className={styles.field}>
            <label>Supplier *</label>
            <input
              type="text"
              placeholder="Search or select supplier"
              value={form.supplierName}
              onChange={(e) => handleChange("supplierName", e.target.value)}
            />
          </div>
        ) : (
          <div className={styles.field}>
            <label>Customer *</label>
            <input
              type="text"
              placeholder="Search or select customer"
              value={form.customerName}
              onChange={(e) => handleChange("customerName", e.target.value)}
            />
          </div>
        )}

        <div className={styles.field}>
          <label>Booking Reference *</label>
          <input
            type="text"
            placeholder="e.g. BK-2024-001234"
            value={form.bookingRef}
            onChange={(e) => handleChange("bookingRef", e.target.value)}
          />
        </div>

        {isSupplier && (
          <div className={styles.field}>
            <label>Component Type</label>
            <SelectBox
              value={form.componentType}
              options={componentTypes}
              onChange={(e) => handleChange("componentType", e.target.value)}
            />
          </div>
        )}

        <div className={styles.field}>
          <label>Reason *</label>
          <SelectBox
            value={form.reason}
            options={isSupplier ? supplierReasons : clientReasons}
            onChange={(e) => handleChange("reason", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Amount *</label>
          <div className={styles.amountRow}>
            <input
              type="number"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              min="0"
              step="0.01"
            />
            <span className={styles.currencyTag}>{form.currency}</span>
          </div>
        </div>

        <div className={styles.field}>
          <label>Date *</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => handleChange("date", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Account</label>
          <SelectBox
            value={form.account}
            options={accounts}
            onChange={(e) => handleChange("account", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Payment Method</label>
          <SelectBox
            value={form.paymentMethod}
            options={paymentMethods}
            onChange={(e) => handleChange("paymentMethod", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Airline PNR</label>
          <input
            type="text"
            placeholder="e.g. ABC123"
            value={form.airlinePnr}
            onChange={(e) => handleChange("airlinePnr", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Supplier Reference No</label>
          <input
            type="text"
            placeholder="e.g. SUP-REF-001"
            value={form.supplierReferenceNo}
            onChange={(e) => handleChange("supplierReferenceNo", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Transaction ID</label>
          <input
            type="text"
            placeholder="e.g. TXN-20240310-001"
            value={form.transactionId}
            onChange={(e) => handleChange("transactionId", e.target.value)}
          />
        </div>

        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label>Notes</label>
          <textarea
            placeholder="Additional notes about this refund..."
            value={form.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default RegisterRefundModal;